import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, TrendingDown, Wallet, ArrowDownRight, ArrowUpRight, Activity, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { mockData } from '../data/mockData';
import { Button } from '../components/ui/button';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, ComposedChart, Line, Cell } from 'recharts';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-primary text-white rounded-lg px-4 py-3 shadow-xl">
        <p className="text-sm font-medium mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color === '#0A192F' ? '#C5A059' : entry.color }}>
            {entry.name}: {entry.value}M
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export const CashFlowDetailPage = ({ year }) => {
  const { language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  const fcfDetails = mockData.freeCashFlowDetails[year];
  const monthlyData = mockData.finance.monthly[year];
  const prevYearDetails = year > 2024 ? mockData.freeCashFlowDetails[year - 1] : null;

  // FCF Bridge Data
  const fcfBridgeData = [
    { name: 'EBITDA', value: fcfDetails.ebitda, color: '#059669' },
    { name: language === 'pt' ? 'Var. Working Capital' : 'Working Capital Chg', value: fcfDetails.workingCapitalChange, color: fcfDetails.workingCapitalChange >= 0 ? '#059669' : '#DC2626' },
    { name: language === 'pt' ? 'Impostos' : 'Taxes', value: fcfDetails.taxesPaid, color: '#DC2626' },
    { name: language === 'pt' ? 'Fluxo de Caixa Operacional' : 'Operating CF', value: fcfDetails.operatingCashFlow, color: '#3B82F6' },
    { name: 'CAPEX', value: -fcfDetails.capex, color: '#DC2626' },
    { name: language === 'pt' ? 'Fluxo de Caixa Livre' : 'Free Cash Flow', value: fcfDetails.freeCashFlow, color: '#C5A059' },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="cashflow-detail-page">
      {/* Header */}
      <div className="bg-primary text-white px-8 py-6">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-center justify-between mb-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/finance')}
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {language === 'pt' ? 'Finanças' : 'Finance'}
            </Button>
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded hover:bg-white/20 transition-colors"
              data-testid="cashflow-language-toggle"
            >
              <Globe className="w-4 h-4" />
              <span className="text-sm font-medium uppercase">{language}</span>
            </button>
          </div>
          <h1 className="font-serif text-3xl font-bold">
            {language === 'pt' ? 'Fluxo de Caixa Livre' : 'Free Cash Flow'}
          </h1>
          <p className="text-white/70 mt-2">
            {language === 'pt' ? 'Análise detalhada de geração e utilização de caixa' : 'Detailed analysis of cash generation and utilization'}
          </p>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-8 py-8 space-y-8">
        {/* Key Metrics */}
        <div className="grid grid-cols-5 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
            <p className="text-xs text-slate-500 uppercase">EBITDA</p>
            <p className="font-serif text-3xl font-semibold text-primary">{fcfDetails.ebitda}M</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <Activity className="w-5 h-5 text-blue-600" />
              </div>
            </div>
            <p className="text-xs text-slate-500 uppercase">{language === 'pt' ? 'Cash Operacional' : 'Operating CF'}</p>
            <p className="font-serif text-3xl font-semibold text-primary">{fcfDetails.operatingCashFlow}M</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
                <ArrowDownRight className="w-5 h-5 text-red-600" />
              </div>
            </div>
            <p className="text-xs text-slate-500 uppercase">CAPEX</p>
            <p className="font-serif text-3xl font-semibold text-red-600">-{fcfDetails.capex}M</p>
          </div>
          <div className="bg-gradient-to-br from-gold-400 to-gold-600 rounded-xl p-5 text-white">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <Wallet className="w-5 h-5 text-white" />
              </div>
            </div>
            <p className="text-xs uppercase opacity-80">{language === 'pt' ? 'Fluxo de Caixa Livre' : 'Free Cash Flow'}</p>
            <p className="font-serif text-3xl font-semibold">{fcfDetails.freeCashFlow}M</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <ArrowUpRight className="w-5 h-5 text-slate-600" />
              </div>
            </div>
            <p className="text-xs text-slate-500 uppercase">{language === 'pt' ? 'Var. Caixa Líq.' : 'Net Cash Chg'}</p>
            <p className="font-serif text-3xl font-semibold text-primary">{fcfDetails.netCashChange}M</p>
          </div>
        </div>

        {/* FCF Bridge */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-serif text-lg font-semibold text-primary mb-4">
            {language === 'pt' ? 'Ponte EBITDA → Fluxo de Caixa Livre' : 'EBITDA → Free Cash Flow Bridge'}
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={fcfBridgeData} layout="vertical" margin={{ left: 120 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} stroke="#94A3B8" />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} stroke="#94A3B8" width={110} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {fcfBridgeData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Monthly FCF Trend */}
        <div className="grid grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="font-serif text-lg font-semibold text-primary mb-4">
              {language === 'pt' ? 'Fluxo de Caixa Livre Mensal' : 'Monthly Free Cash Flow'}
            </h3>
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={monthlyData}>
                <defs>
                  <linearGradient id="fcfGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#C5A059" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#C5A059" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94A3B8" />
                <YAxis tick={{ fontSize: 11 }} stroke="#94A3B8" />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="freeCashFlow" fill="url(#fcfGradient)" stroke="#C5A059" strokeWidth={2} name="FCF" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="font-serif text-lg font-semibold text-primary mb-4">
              {language === 'pt' ? 'Posição de Caixa' : 'Cash Position'}
            </h3>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={monthlyData}>
                <defs>
                  <linearGradient id="cashGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#059669" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#059669" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94A3B8" />
                <YAxis tick={{ fontSize: 11 }} stroke="#94A3B8" />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="cash" fill="url(#cashGradient)" stroke="#059669" strokeWidth={2} name={language === 'pt' ? 'Caixa' : 'Cash'} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* FCF Ratios */}
        <div className="bg-gradient-to-br from-primary to-navy-800 rounded-xl p-8 text-white">
          <h3 className="font-serif text-xl font-semibold mb-6">
            {language === 'pt' ? 'Rácios de Cash Flow' : 'Cash Flow Ratios'}
          </h3>
          <div className="grid grid-cols-4 gap-6">
            <div className="bg-white/10 rounded-xl p-5">
              <p className="text-sm text-white/70 mb-2">FCF Yield</p>
              <p className="font-serif text-4xl font-semibold">{fcfDetails.ratios.fcfYield}%</p>
              <p className="text-xs text-white/50 mt-2">{language === 'pt' ? 'FCF / Valor Empresa' : 'FCF / Enterprise Value'}</p>
            </div>
            <div className="bg-white/10 rounded-xl p-5">
              <p className="text-sm text-white/70 mb-2">FCF / {language === 'pt' ? 'Receita' : 'Revenue'}</p>
              <p className="font-serif text-4xl font-semibold">{fcfDetails.ratios.fcfToRevenue}%</p>
              <p className="text-xs text-white/50 mt-2">{language === 'pt' ? 'Conversão de receita' : 'Revenue conversion'}</p>
            </div>
            <div className="bg-white/10 rounded-xl p-5">
              <p className="text-sm text-white/70 mb-2">{language === 'pt' ? 'Conversão FCF' : 'FCF Conversion'}</p>
              <p className="font-serif text-4xl font-semibold">{fcfDetails.ratios.fcfConversion}%</p>
              <p className="text-xs text-white/50 mt-2">FCF / EBITDA</p>
            </div>
            <div className="bg-white/10 rounded-xl p-5">
              <p className="text-sm text-white/70 mb-2">{language === 'pt' ? 'Ciclo Conversão' : 'Cash Cycle'}</p>
              <p className="font-serif text-4xl font-semibold">{fcfDetails.ratios.cashConversionCycle}</p>
              <p className="text-xs text-white/50 mt-2">{language === 'pt' ? 'dias' : 'days'}</p>
            </div>
          </div>
        </div>

        {/* Cash Uses */}
        <div className="grid grid-cols-3 gap-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h4 className="font-semibold text-primary mb-4">{language === 'pt' ? 'Dividendos' : 'Dividends'}</h4>
            <p className="font-serif text-4xl font-semibold text-red-600">{fcfDetails.dividends}M</p>
            <p className="text-sm text-slate-500 mt-2">
              {fcfDetails.dividends !== 0 
                ? `${Math.abs(Math.round(fcfDetails.dividends / fcfDetails.freeCashFlow * 100))}% ${language === 'pt' ? 'do FCF' : 'of FCF'}`
                : language === 'pt' ? 'Sem dividendos' : 'No dividends'}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h4 className="font-semibold text-primary mb-4">{language === 'pt' ? 'Amortização Dívida' : 'Debt Repayment'}</h4>
            <p className="font-serif text-4xl font-semibold text-red-600">{fcfDetails.debtRepayment}M</p>
            <p className="text-sm text-slate-500 mt-2">
              {Math.abs(Math.round(fcfDetails.debtRepayment / fcfDetails.freeCashFlow * 100))}% {language === 'pt' ? 'do FCF' : 'of FCF'}
            </p>
          </div>
          <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-6">
            <h4 className="font-semibold text-emerald-700 mb-4">{language === 'pt' ? 'Variação Caixa Líquida' : 'Net Cash Change'}</h4>
            <p className="font-serif text-4xl font-semibold text-emerald-600">+{fcfDetails.netCashChange}M</p>
            <p className="text-sm text-emerald-600 mt-2">
              {language === 'pt' ? 'Aumento da posição de caixa' : 'Cash position increase'}
            </p>
          </div>
        </div>

        {/* Year Comparison */}
        {prevYearDetails && (
          <div className="bg-slate-100 rounded-xl p-6">
            <h3 className="font-serif text-lg font-semibold text-primary mb-4">
              {language === 'pt' ? 'Comparativo' : 'Comparison'} {year} vs {year - 1}
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">{language === 'pt' ? 'Métrica' : 'Metric'}</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">{year - 1}</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">{year}</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">{language === 'pt' ? 'Variação' : 'Change'}</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: 'EBITDA', prev: prevYearDetails.ebitda, curr: fcfDetails.ebitda },
                    { label: language === 'pt' ? 'Fluxo de Caixa Operacional' : 'Operating CF', prev: prevYearDetails.operatingCashFlow, curr: fcfDetails.operatingCashFlow },
                    { label: 'CAPEX', prev: prevYearDetails.capex, curr: fcfDetails.capex },
                    { label: language === 'pt' ? 'Fluxo de Caixa Livre' : 'Free Cash Flow', prev: prevYearDetails.freeCashFlow, curr: fcfDetails.freeCashFlow },
                    { label: language === 'pt' ? 'Conversão FCF' : 'FCF Conversion', prev: prevYearDetails.ratios.fcfConversion, curr: fcfDetails.ratios.fcfConversion, isPercent: true },
                  ].map((row, idx) => {
                    const change = row.isPercent ? (row.curr - row.prev).toFixed(1) : Math.round((row.curr / row.prev - 1) * 100);
                    return (
                      <tr key={idx} className="border-b border-slate-200">
                        <td className="py-3 px-4 font-medium text-primary">{row.label}</td>
                        <td className="py-3 px-4 text-right text-slate-600">{row.prev}{row.isPercent ? '%' : 'M'}</td>
                        <td className="py-3 px-4 text-right font-semibold text-primary">{row.curr}{row.isPercent ? '%' : 'M'}</td>
                        <td className={`py-3 px-4 text-right font-semibold ${change >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {change >= 0 ? '+' : ''}{change}{row.isPercent ? 'pp' : '%'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CashFlowDetailPage;
